import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Collapse,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Choose, classNames, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { usePrevious } from "react-use";
import { setSelectedNavCollapse } from "~/redux/reducers/generalReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { MainMenuItem, useSharedStyles } from "./Navigator";

interface Props {
  item: MainMenuItem;
  nestLevel: number;
  children: ReactNode;
  minified: boolean;
  isInCollapseAndMinified: boolean;
  defaultOpen?: boolean;
}

const NavigatorButton = ({
  item,
  nestLevel,
  children,
  minified,
  isInCollapseAndMinified,
  defaultOpen,
}: Props): JSX.Element => {
  const classes = useSharedStyles();
  const { t } = useTranslation("navigation");
  const [open, setOpen] = useState<boolean>(defaultOpen ?? false);
  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
  const { pathname } = useLocation();
  const mainListItemRef = useRef<HTMLDivElement>(null);
  const navMenuOpen = useAppSelector((s) => s.general.navMenuOpen);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedNavCollapse = useAppSelector(
    (x) => x.general.selectedNavCollapse,
  );
  const dispatch = useAppDispatch();
  const prevMinified = usePrevious(minified);

  useEffect(() => {
    if (pathname.split("/").includes(item.id) && !minified) {
      setOpen(true);
      if (mainListItemRef.current) {
        setAnchorEl(mainListItemRef.current);
      }
    }
  }, [pathname, item.id, minified]);

  useEffect(() => {
    if (!minified) return;
    setOpen(selectedNavCollapse === item.id);
  }, [selectedNavCollapse, item.id, minified]);

  useEffect(() => {
    if (minified && !prevMinified) setOpen(false);
  }, [prevMinified, minified]);

  return (
    <React.Fragment key={item.id}>
      <ListItemButton
        ref={mainListItemRef}
        className={classNames(
          classes.item,
          pathname.split("/").includes(item.id) &&
            (!open || minified) &&
            classes.itemActiveItem,
        )}
        style={{
          paddingLeft: `${
            pathname.split("/").includes(item.id) && (!open || minified)
              ? 16 * nestLevel - 2
              : 16 * nestLevel
          }px`,
        }}
        onClick={(e: React.SyntheticEvent) => {
          setAnchorEl(e.currentTarget);
          setOpen(!open);
          dispatch(setSelectedNavCollapse(item.id));
        }}
      >
        <Tooltip title={t(item.translation).toString()}>
          <ListItemIcon className={classes.itemIcon}>{item.icon}</ListItemIcon>
        </Tooltip>
        <If condition={!minified || isInCollapseAndMinified}>
          <ListItemText
            className={classes.itemText}
            classes={{
              primary: classes.itemPrimary,
            }}
          >
            {t(item.translation)}
          </ListItemText>
          {open ? <ExpandLess /> : <ExpandMore />}
        </If>
      </ListItemButton>
      <Choose>
        <Choose.When condition={!minified && !isInCollapseAndMinified}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children}
            </List>
          </Collapse>
        </Choose.When>
        <Choose.When condition={isInCollapseAndMinified || minified}>
          <Popper
            // https://material-ui.com/customization/z-index/
            style={{ zIndex: 1300 }} // Material ui drawer is default z index 1200, so we need to be above that
            placement="right-start"
            open={open && minified && (!isMobile || navMenuOpen)}
            anchorEl={anchorEl}
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <div style={{ backgroundColor: "#18202c" }}>
                  <Collapse
                    in={open && minified && (!isMobile || navMenuOpen)}
                    timeout="auto"
                  >
                    <List component="div" disablePadding>
                      {children}
                    </List>
                  </Collapse>
                </div>
              </Fade>
            )}
          </Popper>
        </Choose.When>
      </Choose>
    </React.Fragment>
  );
};

export default NavigatorButton;
